import logger from '../../../utils/logger';
import { toNumber } from '../../../utils/numbers';

// Tracks which model URLs have a ready session in the worker (readiness marker only).
export const mlSessionCache = new Set<string>();
export const mlModelUrls = new Map<number, string>();
export const mlConfigUrls = new Map<number, string>();
export type MlConfigData = string[] | { features: string[]; model?: string };
export const mlConfigData = new Map<number, MlConfigData>();

export function getModelUrlForBin(binId: number) {
  return mlModelUrls.has(binId) ? mlModelUrls.get(binId) : '';
}
export function getConfigUrlForBin(binId: number) {
  return mlConfigUrls.has(binId) ? mlConfigUrls.get(binId) : '';
}

// ---------------------------------------------------------------------------
// Web Worker – all ONNX session creation and inference run off the main thread
// ---------------------------------------------------------------------------

const worker = new Worker(new URL('./free-fall-worker.ts', import.meta.url));

let messageIdCounter = 0;

type PendingEntry = { resolve: (value: unknown) => void; reject: (reason: unknown) => void };
const pendingMessages = new Map<string, PendingEntry>();

worker.addEventListener('message', (event: MessageEvent) => {
  const { data } = event;
  const pending = pendingMessages.get(data.id);
  if (!pending) return;
  pendingMessages.delete(data.id);
  if (data.success) {
    pending.resolve(data);
  } else {
    pending.reject(new Error(data.error));
  }
});

function rejectAllPending(reason: unknown): void {
  // @ts-ignore
  for (const pending of pendingMessages.values()) {
    pending.reject(reason);
  }
  pendingMessages.clear();
}

worker.addEventListener('error', (event: ErrorEvent) => {
  rejectAllPending(new Error(`Worker error: ${event.message}`));
});

worker.addEventListener('messageerror', () => {
  rejectAllPending(new Error('Worker message deserialization error'));
});

const WORKER_MESSAGE_TIMEOUT_MS = 30_000;

function sendWorkerMessage<T>(message: Record<string, unknown>): Promise<T> {
  const id = String(++messageIdCounter);
  return new Promise<T>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const settle = (fn: () => void) => {
      clearTimeout(timer);
      fn();
    };
    timer = setTimeout(() => {
      if (pendingMessages.delete(id)) {
        reject(new Error(`Worker message timed out (id=${id}, type=${message['type']})`));
      }
    }, WORKER_MESSAGE_TIMEOUT_MS);
    pendingMessages.set(id, {
      resolve: (value: unknown) => settle(() => resolve(value as T)),
      reject: (reason: unknown) => settle(() => reject(reason)),
    });
    worker.postMessage({ ...message, id });
  });
}

// ---------------------------------------------------------------------------
// Public API – unchanged signatures, logic delegated to the worker
// ---------------------------------------------------------------------------

export const initPredictionSession = async (binId: number): Promise<void> => {
  const modelUrl = getModelUrlForBin(binId);
  const configUrl = getConfigUrlForBin(binId);

  if (!modelUrl) {
    throw new Error(`No model URL found for binId: ${binId}`);
  }

  if (!configUrl) {
    throw new Error(`No config URL found for binId: ${binId}`);
  }

  const result = await sendWorkerMessage<{ config: unknown }>({
    type: 'INIT_SESSION',
    binId,
    modelUrl,
    configUrl,
  });

  mlConfigData.set(binId, result.config as MlConfigData);
  logger.log('config', binId, result.config);
  mlSessionCache.add(modelUrl);
};

function jsonToFilteredArray(data: Record<string, unknown>, featureList: string[]): (unknown | null)[] {
  return featureList.map((feature) => (feature in data ? data[feature] : null));
}

export async function getPredictionFromModel(
  binId: number,
  feedMetrics: Record<string, unknown>,
): Promise<number> {
  const config: MlConfigData = mlConfigData.has(binId) ? mlConfigData.get(binId)! : [];
  let featureList = [];
  if (Array.isArray(config)) {
    featureList = config;
  } else {
    featureList = config?.features;
  }
  logger.debug('features from config', binId, featureList, config);

  if (!featureList?.length) {
    throw new Error(`No features found for binId: ${binId}`);
  }

  const features = jsonToFilteredArray(feedMetrics, featureList);
  const featureValues = features.map(v => (v === null ? 0 : Number(v)));

  const modelUrl = getModelUrlForBin(binId);

  if (!modelUrl) {
    throw new Error(`No model URL found for binId: ${binId}`);
  }

  if (!mlSessionCache.has(modelUrl)) {
    throw new Error(`Model session is not ready for binId: ${binId}`);
  }

  const result = await sendWorkerMessage<{ prediction: number }>({
    type: 'PREDICT',
    binId,
    modelUrl,
    features: featureValues,
  });

  const prediction = toNumber(result.prediction);
  logger.debug('prediction from model', prediction, binId, features, feedMetrics);

  return prediction;
}
